import { Children, FC, MutableRefObject, useCallback, useRef, useState } from "react";
import { ControlProps } from "react-select";

import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import classNames from "classnames";

import { useValidity } from "../../../ValidityGroup";
import { CustomReactSelectProps } from "../../CustomReactSelectProps";
import { OptionItem } from "../../OptionItem";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedDropdownControlStyles = createComponentStyleHook<ControlProps<OptionItem | null>>({
  legacyStyles,
  tokenStyles,
  componentTokens: "dropdown_control",
});

/**
 * The control component.
 */
export const Control: FC<ControlProps<OptionItem | null>> = (props) => {
  const { children, selectProps, innerProps, innerRef, isFocused, isDisabled, menuIsOpen } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedDropdownControlStyles(props);
  const [value, ...components] = Children.toArray(children);

  const [invalid] = useValidity();
  const [hover, setHover] = useState(false);

  const rootClassName = classNames(styles.Control, "control", {
    focused: isFocused || menuIsOpen,
    invalid,
    hover,
    readonly: (selectProps as CustomReactSelectProps).isReadOnly,
    disabled: isDisabled,
    opened: menuIsOpen,
  });

  const controlRef = useRef<HTMLDivElement | null>(null);
  // Cast to CustomReactSelectProps to safely access aria-label (selectProps is typed as any)
  const ariaLabel = (selectProps as CustomReactSelectProps)["aria-label"];
  // Cast to CustomReactSelectProps to safely access aria-labelledby (selectProps is typed as any)
  const ariaLabelledBy = (selectProps as CustomReactSelectProps)["aria-labelledby"];
  // Cast to CustomReactSelectProps to safely access isReadOnly (selectProps is typed as any)
  const readOnly = (selectProps as CustomReactSelectProps).isReadOnly;

  const labelMouseEnterHandler = useCallback((ev: Event) => {
    setHover(true);
    ev.stopPropagation();
  }, []);

  const labelMouseExitHandler = useCallback((ev: Event) => {
    setHover(false);
    ev.stopPropagation();
  }, []);

  const controlRefHandler = useCallback((ref: HTMLDivElement | null) => {
    if (controlRef.current) {
      controlRef.current.removeEventListener("cust_labelMouseEnter", labelMouseEnterHandler);
      controlRef.current.removeEventListener("cust_labelMouseExit", labelMouseExitHandler);
    }

    if (ref) {
      ref.addEventListener("cust_labelMouseEnter", labelMouseEnterHandler);
      ref.addEventListener("cust_labelMouseExit", labelMouseExitHandler);
    } else {
      setHover(false);
    }

    controlRef.current = ref;

    if (innerRef) {
      if (typeof innerRef === "function") {
        innerRef(ref);
      } else {
        (innerRef as MutableRefObject<HTMLDivElement | null>).current = ref;
      }
    }
  }, []);

  return (
    <StyleModeBoundary mode={mode}>
      <div
        {...innerProps}
        ref={controlRefHandler}
        role="combobox"
        aria-expanded={menuIsOpen}
        aria-disabled={isDisabled}
        aria-readonly={readOnly}
        aria-invalid={invalid}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        <Shelf className={rootClassName} spacing="none">
          {isDisabled && <div className={styles.DisabledPlaceholderContainer}>&nbsp;</div>}
          {!isDisabled && (
            <>
              {(selectProps as CustomReactSelectProps).prefix && (
                <div className={classNames(styles.Prefix, "prefix")}>{(selectProps as CustomReactSelectProps).prefix}</div>
              )}
              {value}
              {(selectProps as CustomReactSelectProps).suffix && (
                <div className={classNames(styles.Suffix, "suffix")}>{(selectProps as CustomReactSelectProps).suffix}</div>
              )}
              {components}
            </>
          )}
        </Shelf>
      </div>
    </StyleModeBoundary>
  );
};
